import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, useToast } from '@/components/ui';
import MultiSelect from '@/components/ui/MultiSelect';
import CronScheduler from '@/components/ui/CronScheduler';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  campaignsService,
  type CreateCampaignRequest,
  type ScriptQuestion,
} from '@/services/api/campaigns';
import { ArrowLeft, ChevronDown, FileText, Megaphone } from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const LEAD_CATEGORY_OPTIONS = [
  { value: 'medicare', label: 'Medicare' },
  { value: 'life_insurance', label: 'Life Insurance' },
  { value: 'dental', label: 'Dental' },
  { value: 'health', label: 'Health' },
  { value: 'auto', label: 'Auto Insurance' },
  { value: 'home', label: 'Home Insurance' },
  { value: 'seniors', label: 'Seniors' },
  { value: 'family', label: 'Family' },
  { value: 'business', label: 'Business' },
];

const buildScriptFromText = (raw: string): ScriptQuestion[] =>
  raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((question, idx) => ({
      id: `q-${Date.now()}-${idx}`,
      question,
      type: 'text',
      required: false,
    }));

// ── Component ─────────────────────────────────────────────────────────────────
const AddCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [agents, setAgents] = useState<{ uuid: string; display_name: string }[]>([]);
  const [scriptText, setScriptText] = useState('');

  useEffect(() => {
    campaignsService.getAvailableAgents().then((res: any) => {
      setAgents(res?.attributes?.callingAgents ?? []);
    });
  }, []);

  const agentOptions = agents.map((a) => ({
    value: a.uuid,
    label: a.display_name,
  }));

  const formik = useFormik<CreateCampaignRequest>({
    initialValues: {
      name: '',
      description: '',
      status: 'draft',
      lead_categories: [],
      assigned_agent_ids: [],
      script: [],
      scheduled_at: '0 9 * * *',  // defaults to Daily at 9 AM
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Campaign name is required'),
      lead_categories: Yup.array().min(1, 'Select at least one lead category'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const validScript = buildScriptFromText(scriptText);
      try {
        await campaignsService.create({ ...values, script: validScript });
        success('Campaign created successfully!');
        navigate('/campaigns');
      } catch (e: any) {
        toastError(e?.message || 'Failed to create campaign');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/campaigns')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft size={18} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={22} className="text-indigo-600" />
            Add Campaign
          </h1>
          <p className="text-xs sm:text-sm text-gray-500">
            Create a new AI calling campaign with script and agent assignment.
          </p>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        {/* ── Section 1: Basic Info ─────────────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">1</span>
            Basic Information
          </h2>

          {/* Name */}
          <div>
            <label className="text-xs font-medium mb-1 block text-gray-700">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <Input
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g. Medicare Outreach Q3"
            />
            {formik.touched.name && formik.errors.name && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium mb-1 block text-gray-700">Description</label>
            <textarea
              name="description"
              value={formik.values.description ?? ''}
              onChange={formik.handleChange}
              rows={2}
              placeholder="Brief description of this campaign…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-medium mb-1 block text-gray-700">Status</label>
            <div className="relative">
              <select
                name="status"
                value={formik.values.status ?? 'draft'}
                onChange={formik.handleChange}
                className="w-full h-10 border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="completed">Completed</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Cron Schedule */}
          <CronScheduler
            label="Recurring Schedule"
            value={(formik.values.scheduled_at ?? '0 9 * * *').toString()}
            onChange={(cron) => formik.setFieldValue('scheduled_at', cron)}
          />
        </div>

        {/* ── Section 2: Targeting ──────────────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">2</span>
            Targeting & Assignment
          </h2>

          {/* Lead Categories */}
          <div>
            <label className="text-xs font-medium mb-1 block text-gray-700">
              Lead Categories <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              options={LEAD_CATEGORY_OPTIONS}
              value={formik.values.lead_categories ?? []}
              onChange={(val) => formik.setFieldValue('lead_categories', val)}
              placeholder="Select lead categories…"
            />
            {formik.touched.lead_categories && formik.errors.lead_categories && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.lead_categories as string}</p>
            )}
          </div>

          {/* Assign Agents */}
          <div>
            <label className="text-xs font-medium mb-1 block text-gray-700">Assign Agents</label>
            <MultiSelect
              options={agentOptions}
              value={formik.values.assigned_agent_ids ?? []}
              onChange={(val) => formik.setFieldValue('assigned_agent_ids', val)}
              placeholder={agentOptions.length === 0 ? 'Loading agents…' : 'Select agents to assign…'}
            />
          </div>
        </div>

        {/* ── Section 3: Script ─────────────────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">3</span>
            <FileText size={14} className="text-indigo-500" />
            Call Script (Questions)
          </h2>
          <textarea
            value={scriptText}
            onChange={(e) => setScriptText(e.target.value)}
            rows={8}
            placeholder={'Enter one question per line.\nExample:\nAre you currently enrolled in Medicare?\nWould you like to learn about supplement plans?'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
          />
          <p className="text-xs text-gray-500">
            One line = one question. This will be converted to campaign script questions automatically.
          </p>
        </div>

        {/* ── Actions ───────────────────────────────────────────────────────── */}
        <div className="flex justify-end gap-3 pb-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/campaigns')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={formik.isSubmitting}
            className="bg-primary-button hover:bg-primary-button text-white font-semibold px-6"
          >
            {formik.isSubmitting ? 'Creating…' : 'Create Campaign'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddCampaignPage;
